import { HttpError, httpErrorSchema, NormalizedError, normalizedMessageSchema } from '../../modules/backend/errors';
import { Language } from '../i18n/constants';

type Options = {
  url: string;
  language: Language;
  headers?: HeadersInit;
};

/**
 * This function is used to create a data fetcher for Tanstack query and should not be used for mutations.
 */
export const makeAnonymousFetcher =
  <T>({ url, language, headers }: Options) =>
  async (): Promise<T> => {
    const requestOptions: RequestInit = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept-Language': language,
        ...headers,
      },
    };

    const response = await fetch(url, requestOptions);

    // Tanstack query requires us to throw an error, if the request was not successful
    // https://tanstack.com/query/v4/docs/react/guides/query-functions#usage-with-fetch-and-other-clients-that-do-not-throw-by-default

    let data: unknown;

    try {
      data = await response.json();
      // TODO: @Simon consider using error object
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      throw new HttpError(response.statusText, response.status);
    }

    if (response.status === 400) {
      const result = normalizedMessageSchema.safeParse(data);
      if (result.success) {
        throw new NormalizedError(result.data.messages);
      }
    }

    if (!response.ok) {
      const result = httpErrorSchema.safeParse(data);
      if (!result.success) {
        throw new HttpError(response.statusText, response.status);
      }

      const { error, message, statusCode } = result.data;

      throw new HttpError(error, statusCode, message);
    }

    return data as T;
  };
